import React, { useContext, useMemo } from 'react';

// DEPENDENCIES
import isUndefined from 'lodash/isUndefined';
import orderBy from 'lodash/orderBy';

// GLOBAL FUNCTIONS
import { formatDollar } from 'functions.js';

// GLOBAL VARIABLES
import { NEW_USED } from 'codes';

// GLOBAL HELPERS/CONTEXT
import { RecordContext } from 'helpers/getRecordData';
import useRecordView from 'helpers/getRecordView';

// GLOBAL COMPONENTS
import Form from 'core/form/Form';
import Card from 'parts/card/Card';

// LOCAL COMPONENTS
import InputAppendCol from '../../../sections/parts/InputAppendCol';
import RepairInvoice from 'routers/interface/sections/parts/RepairInvoice';
import DmsTooltip from '../tools/DmsTooltip';

//STYLES
import styles from './inventoryInternal.module.scss';

// MAIN COMPONENT
const InventoryInternalForm = () => {

  // CONTEXT
  const { record: { inventory = {}, ...record } = {} } = useContext(RecordContext);
  const { isAdd, isView } = useRecordView();

  // MEMOS
  const {
    new_used,
    aryInventoryDealerPack,
    aryRepairInvoice,
    dms_invoice_price,
    dmsinvoice,
    invoice_cost,
    invoice_cost_with_repair_invoice,
    invoice_cost_with_inventory_pack,
    invoice_cost_with_inventory_pack_dms_invoice_difference,
    inventory_pack_total
  } = useMemo(
    () => isAdd ? record : inventory,
    [record, inventory, isAdd]
  )
  const isNew = useMemo(
    () => parseInt(new_used) === NEW_USED.new.numeric,
    [new_used]
  )
  const type = useMemo(
    () => (!isUndefined(new_used) ? (isNew ? 'Factory Invoice' : 'ACV') : ''),
    [new_used, isNew]
  )
  const invoiceCostLabel = useMemo(() => (type === 'ACV' ? type : `${type} Cost`), [type]);
  const invoiceCostWithInventoryPackDMSInvoiceDifference = useMemo(
    () => invoice_cost_with_inventory_pack_dms_invoice_difference < 0
      ? `(${formatDollar(Math.abs(invoice_cost_with_inventory_pack_dms_invoice_difference))})`
      : formatDollar(invoice_cost_with_inventory_pack_dms_invoice_difference),
    [invoice_cost_with_inventory_pack_dms_invoice_difference]
  )
  const showAccountToCredit = useMemo(() => {
    return !isView || (aryInventoryDealerPack || []).some(a => a.item_account_to_credit);
  }, [isView, aryInventoryDealerPack]);

  return (
    <Form.Body>
      <Form.PlainText
        label={invoiceCostLabel}
        value={invoice_cost || 0}
        schema="dollars"
        input={{
          append: showAccountToCredit ? (
            <>
              <InputAppendCol cols={{ sm: 8 }} />
              <InputAppendCol cols={{ sm: 8 }} className="fw-bolder">Account to Credit</InputAppendCol>
            </>
          ) : undefined}}
      />
      {Array.isArray(aryInventoryDealerPack) &&
        orderBy(aryInventoryDealerPack, ['item_name'], ['asc']).map((v, i) => (
          <Form.PlainText
            key={`${v.inventory_dealer_pack_id}-${i}`}
            label={`Inventory Pack ${i + 1}`}
            value={v.item_amount}
            schema="dollars"
            input={{
              append: (
                <>
                  <InputAppendCol cols={{ sm: 8 }}>{v.item_name}</InputAppendCol>
                  <InputAppendCol cols={{ sm: 8 }}>{v.item_account_to_credit}</InputAppendCol>
                </>
              )
            }}
          />
        ))
      }
      <Form.PlainText
        label="Inventory Pack Total"
        value={inventory_pack_total || 0}
        schema="dollars"
      />
      <Form.PlainText
        label={`${type} with Inventory Pack Total`}
        value={invoice_cost_with_inventory_pack || 0}
        schema="dollars"
      />
      <RepairInvoice />
      {Array.isArray(aryRepairInvoice) && !!aryRepairInvoice.length && (
        <Form.PlainText
          label={`${type} with Repair Invoices`}
          value={invoice_cost_with_repair_invoice || 0}
          schema="dollars"
        />
      )}
      <Form.PlainText
        label="DMS Cost"
        suffix={<DmsTooltip />}
        className={styles.span}
        fullWidth
        value={`${dms_invoice_price ? formatDollar(dms_invoice_price) : 'Not available'} ${dmsinvoice}`}
      />
      {dms_invoice_price &&
        <Form.PlainText
          label={`${type} with Inventory Pack Total vs. DMS Cost Difference`}
          value={invoiceCostWithInventoryPackDMSInvoiceDifference}
        />
      }
    </Form.Body>
  );
};

// EXPORT
export default function InventoryInternal(props) {
  return <Card title="Inventory - Internal" body={<InventoryInternalForm {...props} />} />;
}
